// ABOUTME: Reusable status badge component for displaying various activity states.
// ABOUTME: Supports variants: finished, unplanned, planned, skipped, public.

import type { FC, ReactNode } from "react"

export type StatusBadgeVariant = "finished" | "unplanned" | "planned" | "skipped" | "public"
export type StatusBadgeSize = "sm" | "md" | "lg"
/**
 * "solid" fills the chip with the status colour. "dot" keeps the chip neutral
 * and carries the colour in a dot — what a card-sized status looks like in the
 * day view (docs/design/Календарь.dc.html).
 */
export type StatusBadgeAppearance = "solid" | "dot"

interface StatusBadgeProps {
  variant: StatusBadgeVariant
  size?: StatusBadgeSize
  appearance?: StatusBadgeAppearance
  className?: string
  children?: ReactNode
  shrink?: boolean
}

const variantStyles: Record<StatusBadgeVariant, string> = {
  finished: "bg-[#C8F86C] text-[#101010]",
  unplanned: "bg-[#FFF48E] text-[#101010]",
  planned: "bg-customGray-100 text-[#101010] dark:text-foreground",
  skipped: "bg-[#F05352] text-white",
  public: "bg-green-100 text-green-700",
}

/** The same status colours, as a dot instead of a fill. */
const dotStyles: Record<StatusBadgeVariant, string> = {
  finished: "bg-[#C8F86C]",
  unplanned: "bg-[#FFF48E]",
  planned: "bg-customGray-100",
  skipped: "bg-[#F05352]",
  public: "bg-green-500",
}

const sizeStyles: Record<StatusBadgeSize, string> = {
  sm: "text-[10px] px-1.5 py-0.5 rounded",
  md: "text-[10px] px-2 py-1 rounded-full",
  lg: "text-sm px-4 py-2 rounded-full",
}

const defaultLabels: Record<StatusBadgeVariant, string> = {
  finished: "Finished",
  unplanned: "Unplanned",
  planned: "Planned",
  skipped: "Skipped",
  public: "Public",
}

export const StatusBadge: FC<StatusBadgeProps> = ({
  variant,
  size = "md",
  appearance = "solid",
  className = "",
  children,
  shrink = false,
}) => {
  const label = children ?? defaultLabels[variant]

  if (appearance === "dot") {
    return (
      <span
        className={`inline-flex shrink-0 items-center gap-2 rounded-lg border border-border bg-white px-2.5 py-1.5 text-xs whitespace-nowrap text-foreground ${className}`}
      >
        <span className={`size-2.5 flex-none rounded-[50%] ${dotStyles[variant]}`} />
        <span className="truncate">{label}</span>
      </span>
    )
  }

  const baseStyles = shrink ? "inline-block max-w-full truncate font-medium" : "shrink-0 font-medium"
  const variantStyle = variantStyles[variant]
  const sizeStyle = sizeStyles[size]

  return <span className={`${baseStyles} ${variantStyle} ${sizeStyle} ${className}`}>{label}</span>
}
